import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { SAKTI_DEFAULTS } from '../apiConfig';
import SaktiApiService from '../services/saktiApiService';
import SaktiBkmApiService from '../services/saktiBkmApiService';
import { getTodayInJakartaForApi } from '../utils/date';
import {
  SaktiFilterOptions,
  SaktiPaginationMeta,
  SaktiBkmKpiData,
  SaktiBkmListItem,
  SaktiBkmSimulationData,
  SaktiFilters,
} from '../types';

const EMPTY_OPTIONS: SaktiFilterOptions = {
  companies: [],
  estates: [],
  divisions: [],
};

const EMPTY_KPI: SaktiBkmKpiData = {
  total_bkm: 0,
  total_ha: 0,
  total_harvesters: 0,
};

const EMPTY_PAGINATION: SaktiPaginationMeta = {
  page: 1,
  limit: SAKTI_DEFAULTS.DEFAULT_LIMIT,
  total_items: 0,
  total_pages: 0,
};

interface UseSaktiBkmDashboardResult {
  options: SaktiFilterOptions;
  loadingOptions: boolean;
  loadingKpi: boolean;
  loadingTable: boolean;
  draftFilters: SaktiFilters;
  appliedFilters: SaktiFilters;
  kpi: SaktiBkmKpiData;
  rows: SaktiBkmListItem[];
  pagination: SaktiPaginationMeta;
  tableError: string | null;
  simulationData: SaktiBkmSimulationData | null;
  loadingSimulation: boolean;
  onDraftFilterChange: (nextFilters: SaktiFilters) => void;
  applyFilters: () => Promise<void>;
  goToPage: (page: number) => Promise<void>;
  fetchSimulation: (divisionId: number, date: string) => Promise<void>;
  reloadAll: () => Promise<void>;
}

export const useSaktiBkmDashboard = (): UseSaktiBkmDashboardResult => {
  const toast = useToast();

  const initialFilters: SaktiFilters = useMemo(() => ({
    date: getTodayInJakartaForApi(),
    company_id: SAKTI_DEFAULTS.DEFAULT_COMPANY_ID,
  }), []);

  const [options, setOptions] = useState<SaktiFilterOptions>(EMPTY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);
  const [loadingKpi, setLoadingKpi] = useState<boolean>(true);
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [draftFilters, setDraftFilters] = useState<SaktiFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<SaktiFilters>(initialFilters);
  const [kpi, setKpi] = useState<SaktiBkmKpiData>(EMPTY_KPI);
  const [rows, setRows] = useState<SaktiBkmListItem[]>([]);
  const [pagination, setPagination] = useState<SaktiPaginationMeta>(EMPTY_PAGINATION);
  const [tableError, setTableError] = useState<string | null>(null);
  const [simulationData, setSimulationData] = useState<SaktiBkmSimulationData | null>(null);
  const [loadingSimulation, setLoadingSimulation] = useState<boolean>(false);

  const fetchData = useCallback(async (filters: SaktiFilters, page: number = 1) => {
    setLoadingTable(true);
    setTableError(null);
    try {
      const params = { ...filters, page, limit: SAKTI_DEFAULTS.DEFAULT_LIMIT };
      const response = await SaktiBkmApiService.getList(params);
      if (response.success) {
        setRows(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      setTableError(error instanceof Error ? error.message : 'Gagal memuat data BKM');
    } finally {
      setLoadingTable(false);
    }
  }, []);

  const fetchKpi = useCallback(async (filters: SaktiFilters) => {
    setLoadingKpi(true);
    try {
      const response = await SaktiBkmApiService.getKpi(filters);
      if (response.success) {
        setKpi(response.data);
      }
    } catch (error) {
      // toast
    } finally {
      setLoadingKpi(false);
    }
  }, []);

  const loadFilterOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const fetchedOptions = await SaktiApiService.fetchFilterOptions();
      setOptions(fetchedOptions);
      
      const defaultCompany = fetchedOptions.companies.find(c => c.id === SAKTI_DEFAULTS.DEFAULT_COMPANY_ID)?.id 
        || fetchedOptions.companies[0]?.id 
        || SAKTI_DEFAULTS.DEFAULT_COMPANY_ID;

      const nextFilters = { ...initialFilters, company_id: defaultCompany };
      setDraftFilters(nextFilters);
      setAppliedFilters(nextFilters);
      
      fetchKpi(nextFilters);
      fetchData(nextFilters);
    } finally {
      setLoadingOptions(false);
    }
  }, [initialFilters, fetchKpi, fetchData]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const onDraftFilterChange = useCallback((nextFilters: SaktiFilters) => {
    setDraftFilters(nextFilters);
  }, []);

  const applyFilters = useCallback(async () => {
    setAppliedFilters(draftFilters);
    fetchKpi(draftFilters);
    fetchData(draftFilters, 1);
  }, [draftFilters, fetchKpi, fetchData]);

  const goToPage = useCallback(async (page: number) => {
    fetchData(appliedFilters, page);
  }, [appliedFilters, fetchData]);

  const fetchSimulation = useCallback(async (divisionId: number, date: string) => {
    setLoadingSimulation(true);
    try {
      const response = await SaktiBkmApiService.getSimulation(divisionId, date);
      if (response.success) {
        setSimulationData(response.data);
      }
    } catch (error) {
      toast({
        title: 'Gagal memuat simulasi',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingSimulation(false);
    }
  }, [toast]);

  const reloadAll = useCallback(async () => {
    fetchKpi(appliedFilters);
    fetchData(appliedFilters, pagination.page);
  }, [appliedFilters, pagination.page, fetchKpi, fetchData]);

  return {
    options,
    loadingOptions,
    loadingKpi,
    loadingTable,
    draftFilters,
    appliedFilters,
    kpi,
    rows,
    pagination,
    tableError,
    simulationData,
    loadingSimulation,
    onDraftFilterChange,
    applyFilters,
    goToPage,
    fetchSimulation,
    reloadAll,
  };
};

export default useSaktiBkmDashboard;
